import { useState } from 'react';
import { Link } from 'react-router';
import Sheet from './Sheet';
import Icon from './Icon';
import { PRIMARY, CreditsNote } from './CaptureSheets';
import { LOCALE, kcal } from '../lib/format';
import { useAiCredits } from '../lib/credits';
import { useApp, useAppPath } from '../lib/AppContext';
import { ageOn, today } from '../lib/dates';
import { ApplyFromChoice, askFrom } from './Targets';
import DateField, { DOB_DEFAULT_MONTH } from './DateField';

const GOALS = [
  { id: 'lose_fat', label: 'Giảm mỡ', text: 'Giảm cân nhưng vẫn giữ cơ' },
  { id: 'maintain', label: 'Giữ cân', text: 'Duy trì vóc dáng, ăn uống cân bằng' },
  { id: 'gain_muscle', label: 'Tăng cơ', text: 'Tăng cân có kiểm soát, ưu tiên cơ' },
  { id: 'other', label: 'Khác', text: 'Tự mô tả mục tiêu của bạn' },
];
const NOTE_MAX = 300;
const DISCLAIMER = 'Gợi ý mang tính tham khảo, không thay thế tư vấn của bác sĩ hay chuyên gia dinh dưỡng.';

// Form → AI suggestion → apply. `profile` has weight, height, training days, sex, age and the current targets.
function GoalSuggestion({ profile, onApplied, onClose }) {
  const { api } = useApp();
  const path = useAppPath();
  const credits = useAiCredits();
  const [sex, setSex] = useState(profile.sex || '');
  const [dob, setDob] = useState(profile.date_of_birth || '');
  const [goal, setGoal] = useState('lose_fat');
  const [note, setNote] = useState('');
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState(null);
  // Applying after meals were logged today asks whether the new targets start today or tomorrow.
  const [from, setFrom] = useState('today');

  // Sex and date of birth come from the profile; the form only asks for whichever is missing.
  const askSex = !profile.sex;
  const askDob = !profile.date_of_birth;
  const summary = [
    profile.sex && (profile.sex === 'male' ? 'Nam' : 'Nữ'),
    profile.date_of_birth && `${ageOn(profile.date_of_birth)} tuổi`,
    profile.weight_kg && `${Number(profile.weight_kg).toLocaleString(LOCALE)} kg`,
    profile.height_cm && `${profile.height_cm} cm`,
    `${profile.training_days_per_week || 0} buổi/tuần`,
  ].filter(Boolean).join(' · ');

  const suggest = async (event) => {
    event?.preventDefault();
    if (credits.blocked(suggest)) return;
    setBusy('suggest');
    setError(null);
    try {
      setResult(await api.post('/goal_suggestions', { sex, date_of_birth: dob, goal, note: goal === 'other' ? note : undefined }, { idempotencyKey: credits.key() }));
      credits.used();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(null);
    }
  };

  const apply = async () => {
    setBusy('apply');
    setError(null);
    try {
      await api.post(`/goal_suggestions/${result.id}/apply`, { from });
      onApplied(result.result, from);
    } catch (e) {
      setError(e.message);
      setBusy(null);
    }
  };

  if (!profile.weight_kg || !profile.height_cm) {
    return (
      <div className="flex flex-col gap-4">
        <p className="text-sm">AI cần cân nặng và chiều cao của bạn để gợi ý mục tiêu.</p>
        <Link to={path('/profile')} className={PRIMARY} onClick={onClose}>Cập nhật hồ sơ</Link>
      </div>
    );
  }

  if (result) {
    const r = result.result;
    const rows = [
      { label: 'Calo mỗi ngày', old: profile.calorie_goal, value: r.calories, unit: 'kcal', accent: '' },
      { label: 'Đạm ngày nghỉ', old: profile.protein_goal, value: r.protein_rest_g, unit: 'g', accent: 'text-protein' },
      { label: 'Đạm ngày tập', old: profile.workout_protein_goal, value: r.protein_training_g, unit: 'g', accent: 'text-protein' },
    ];
    return (
      <div className="flex flex-col gap-4">
        <span className="inline-flex w-fit items-center gap-1.5 rounded-full bg-[#e3f1e9] px-3 py-1 text-xs font-semibold text-[#17804f]">
          <Icon name="sparkles" className="size-3.5" /> Mục tiêu: {result.goal_label}
        </span>
        <dl className="divide-y divide-line rounded-[16px] border border-line px-4">
          {rows.map((row) => (
            <div key={row.label} className="flex items-center justify-between gap-3 py-3">
              <dt className="text-sm">{row.label}</dt>
              <dd className="tabular flex items-baseline gap-2">
                {row.old && Number(row.old) !== row.value && <s className="text-xs text-muted">{kcal(row.old)}</s>}
                <b className={`text-lg font-extrabold ${row.accent}`}>{kcal(row.value)}</b>
                <span className="text-xs text-muted">{row.unit}</span>
              </dd>
            </div>
          ))}
        </dl>
        <div>
          <p className="text-sm font-bold">Vì sao?</p>
          <ul className="mt-1 list-disc space-y-1 pl-5 text-sm">{r.reasons.map((reason) => <li key={reason}>{reason}</li>)}</ul>
        </div>
        {askFrom(profile.today) && (
          <div className="flex flex-col gap-2">
            <p className="text-sm font-bold">Áp dụng từ khi nào?</p>
            <ApplyFromChoice info={profile.today} value={from} onChange={setFrom}
              next={{ calorie_goal: r.calories, protein_goal: r.protein_rest_g, workout_protein_goal: r.protein_training_g }} />
          </div>
        )}
        {error && <p role="alert" className="text-sm font-medium text-error">{error}</p>}
        <div className="flex flex-col gap-2">
          <button type="button" className={PRIMARY} disabled={!!busy} onClick={apply}>
            {busy === 'apply' && <span className="loading loading-spinner loading-sm" />} Áp dụng vào mục tiêu
          </button>
          <button type="button" className="btn h-12 rounded-2xl border-line bg-base-100 font-semibold" disabled={!!busy}
            onClick={() => setResult(null)}>
            Thử mục tiêu khác
          </button>
          <p className="text-center text-xs text-muted">Bạn vẫn sửa được từng con số sau khi áp dụng.</p>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={suggest} className="flex flex-col gap-4">
      <div className="flex items-center justify-between gap-3 rounded-[14px] bg-base-200 px-4 py-3">
        <div>
          <div className="text-xs text-muted">Dựa trên hồ sơ của bạn</div>
          <div className="text-sm font-bold">{summary}</div>
        </div>
        <Link to={path('/profile')} className="text-sm font-semibold text-brand-text" onClick={onClose}>Sửa</Link>
      </div>

      {(askSex || askDob) && (
      <div className={`grid gap-3 ${askSex && askDob ? 'grid-cols-2' : 'grid-cols-1'}`}>
        {askSex && (
        <div className="flex flex-col gap-1.5">
          <span className="text-sm font-semibold">Giới tính</span>
          <div role="radiogroup" aria-label="Giới tính" className="grid grid-cols-2 gap-1 rounded-[12px] bg-base-200 p-1">
            {[['male', 'Nam'], ['female', 'Nữ']].map(([id, label]) => (
              <button key={id} type="button" role="radio" aria-checked={sex === id} onClick={() => setSex(id)}
                className={`h-9 rounded-[9px] text-sm font-semibold ${sex === id ? 'bg-neutral text-neutral-content' : ''}`}>
                {label}
              </button>
            ))}
          </div>
        </div>
        )}
        {askDob && (
        <div className="flex flex-col gap-1.5">
          <label htmlFor="goal-dob" className="text-sm font-semibold">Ngày sinh{dob ? <span className="font-normal text-muted"> · {ageOn(dob)} tuổi</span> : null}</label>
          <DateField id="goal-dob" title="Ngày sinh" min="1900-01-01" max={today()} value={dob} onChange={setDob}
            defaultMonth={DOB_DEFAULT_MONTH} startView="years" className="rounded-[12px]" />
        </div>
        )}
      </div>
      )}

      <div role="radiogroup" aria-label="Mục tiêu của bạn là gì?" className="flex flex-col gap-2">
        <span className="text-sm font-semibold">Mục tiêu của bạn là gì?</span>
        {GOALS.map((g) => (
          <button key={g.id} type="button" role="radio" aria-checked={goal === g.id} onClick={() => setGoal(g.id)}
            className={`flex min-h-14 items-center gap-3 rounded-[16px] border-2 px-4 py-2 text-left ${goal === g.id ? 'border-primary bg-[#e3f1e9]/50' : 'border-line'}`}>
            <span className={`grid size-5 shrink-0 place-items-center rounded-full border-2 ${goal === g.id ? 'border-primary' : 'border-base-300'}`}>
              {goal === g.id && <span className="size-2.5 rounded-full bg-primary" />}
            </span>
            <span>
              <span className="block font-semibold">{g.label}</span>
              <span className="block text-xs text-muted">{g.text}</span>
            </span>
          </button>
        ))}
        {goal === 'other' && (
          <label className="flex flex-col gap-1.5">
            <span className="sr-only">Mô tả mục tiêu của bạn</span>
            <textarea autoFocus maxLength={NOTE_MAX} className="textarea min-h-24 w-full rounded-[14px] border-line text-base"
              placeholder="VD: Chuẩn bị chạy marathon sau 3 tháng, muốn giảm 3 kg nhưng vẫn đủ sức tập"
              value={note} onChange={(e) => setNote(e.target.value)} />
            <span className="text-right text-xs text-muted">{note.length}/{NOTE_MAX}</span>
          </label>
        )}
      </div>

      {error && <p role="alert" className="text-sm font-medium text-error">{error}</p>}
      <div className="flex flex-col gap-2">
        <CreditsNote text={credits.note} />
        <button type="submit" className={PRIMARY} disabled={!!busy || !sex || !dob || (goal === 'other' && !note.trim())}>
          {busy === 'suggest' ? <><span className="loading loading-spinner loading-sm" /> AI đang tính…</> : 'Gợi ý cho tôi'}
        </button>
        <p className="text-center text-xs text-muted">{DISCLAIMER}</p>
      </div>
    </form>
  );
}

export default function GoalSuggestSheet({ open, profile, onApplied, onClose }) {
  return (
    <Sheet open={open} onClose={onClose} title="Gợi ý mục tiêu bằng AI">
      {open && <GoalSuggestion profile={profile} onApplied={onApplied} onClose={onClose} />}
    </Sheet>
  );
}
